import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import Logo from "@/components/Logo";
import PlayStoreButton from "@/components/PlayStoreButton";
import { useLanguage } from "@/context/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();
  const productLinks = [
    { label: t.footer.product[0], to: "/#features" },
    { label: t.footer.product[1], to: "/#pricing" },
    { label: t.footer.product[2], to: "/#download" },
  ];
  const companyLinks = [
    { label: t.footer.company[0], to: "/about" },
    { label: t.footer.company[1], to: "/contact" },
  ];
  const legalLinks = [
    { label: t.footer.legal[0], to: "/terms" },
    { label: t.footer.legal[1], to: "/privacy" },
    { label: t.footer.legal[2], to: "/refund" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative mt-32 border-t border-blue-900/10 bg-gradient-to-b from-[#fdfbf7] to-[#f5efe2]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo className="h-12 w-12" />
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              {t.footer.tagline}
            </p>
            <div className="mt-6">
              <PlayStoreButton size="md" testId="footer-cta-playstore" />
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4">
              {t.footer.productTitle}
            </h4>
            <ul className="space-y-2.5">
              {productLinks.map((l) =>
                l.to.startsWith("/#") ? (
                  <li key={l.label}>
                    <a
                      href={l.to.replace("/", "")}
                      className="text-sm text-slate-700 hover:text-blue-900 transition"
                    >
                      {l.label}
                    </a>
                  </li>
                ) : (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-sm text-slate-700 hover:text-blue-900 transition"
                    >
                      {l.label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4">
              {t.footer.companyTitle}
            </h4>
            <ul className="space-y-2.5">
              {companyLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-700 hover:text-blue-900 transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4">
              {t.footer.legalTitle}
            </h4>
            <ul className="space-y-2.5">
              {legalLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace("/", "")}`}
                    className="text-sm text-slate-700 hover:text-blue-900 transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4">
              {t.footer.contactTitle}
            </h4>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2.5">
                <Mail size={16} className="mt-0.5 text-blue-900 shrink-0" />
                <a
                  href="mailto:tnpsctamilapp@gmail.com"
                  data-testid="footer-email"
                  className="hover:text-blue-900 break-all"
                >
                  tnpsctamilapp@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone size={16} className="mt-0.5 text-blue-900 shrink-0" />
                <a
                  href="tel:+919842751334"
                  data-testid="footer-phone"
                  className="hover:text-blue-900"
                >
                  +91 98427 51334
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin size={16} className="mt-0.5 text-blue-900 shrink-0" />
                <span>Tamil Nadu, India</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="laurel-divider mt-14 mb-6">
          <svg
            width="32"
            height="20"
            viewBox="0 0 32 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M16 2c-3 2-5 5-5 8s2 6 5 8c3-2 5-5 5-8s-2-6-5-8z"
              stroke="#EAB308"
              strokeWidth="1.5"
            />
          </svg>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>
            © {new Date().getFullYear()} TNPSC Tamil. {t.footer.rights}
          </p>
          <p>{t.footer.madeIn}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
